import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { HttpService } from './httpService.service';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { environment } from 'src/environments/environment';
import {
  PaymentGatewayConfig,
  PaymentGatewayData,
  PaymentFlowType,
  PaymentContext,
  PaymentPayload,
  CardDetails,
  BillingAddress,
  StripePaymentDetails,
  BraintreeHostedFieldsConfig,
  GatewayKey,
  VaultedPaymentMethod
} from '../interfaces/payment-gateway.interface';

@Injectable({ providedIn: 'root' })
export class PaymentGatewayService {
  private gatewayConfigSubject = new BehaviorSubject<PaymentGatewayData | null>(null);
  readonly gatewayConfig$ = this.gatewayConfigSubject.asObservable();

  private stripeInstance: Stripe | null = null;
  private braintreeClient: any         = null;
  private hostedFieldsInstance: any    = null;
  private dropinInstance: any          = null;
  private vaultManagerInstance: any    = null;
  private hostedFieldsInitPromise: Promise<any> | null = null;

  constructor(private httpService: HttpService) {}

  // ─── Gateway Detection ────────────────────────────────────────────────────

  async detectGateway(customerId?: string): Promise<PaymentGatewayData | null> {
    // Pass isRemoveCustomerId=true to prevent HttpService from adding CustomerId
    // The API expects customer_id (snake_case), not CustomerId
    const body: any = { origin: window.location.origin };
    if (customerId) body.customer_id = customerId;

    try {
      const response = await firstValueFrom(
        this.httpService.post<PaymentGatewayConfig>(
          'api/integration/getDeafultSubscriptionType',
          body,
          false,  // isOnlyCustomBodySend - keep companyId and userID
          true    // isRemoveCustomerId - remove CustomerId from body
        )
      );

      if (!response?.Status || !response?.data) {
        console.warn('[PaymentGateway] Gateway detection returned no data');
        return null;
      }

      this.gatewayConfigSubject.next(response.data);
      return response.data;
    } catch (err) {
      console.error('[PaymentGateway] Gateway detection failed:', err);
      return null;
    }
  }

  determinePaymentFlow(config: PaymentGatewayData): PaymentFlowType {
    switch (config.key as GatewayKey) {
      case 'url':   return PaymentFlowType.STRIPE_REDIRECT;
      case 'token': return PaymentFlowType.BRAINTREE;
      case 'card':  return PaymentFlowType.AUTHORIZE_NET;
      default:      return PaymentFlowType.TRADITIONAL;
    }
  }

  getGatewayConfig(): PaymentGatewayData | null {
    return this.gatewayConfigSubject.value;
  }

  // ─── Stripe ───────────────────────────────────────────────────────────────

  async initializeStripe(): Promise<Stripe | null> {
    if (this.stripeInstance) return this.stripeInstance;
    const key = (environment as any).STRIPE_PUBLISHABLE_KEY;
    if (!key) { console.error('[PaymentGateway] STRIPE_PUBLISHABLE_KEY not configured'); return null; }
    this.stripeInstance = await loadStripe(key);
    return this.stripeInstance;
  }

  async redirectToStripeCheckout(data?: Partial<StripePaymentDetails>): Promise<void> {
    const config = this.gatewayConfigSubject.value;
    if (config?.key !== 'url' || !config.url?.value) {
      throw new Error('Stripe redirect URL not available');
    }
    if (data) {
      localStorage.setItem('stripePaymentDetails', JSON.stringify({
        ...data, timestamp: Date.now()
      }));
    }
    window.location.href = config.url.value;
  }

  handleStripeRedirectReturn(context: PaymentContext): string | null {
    const sessionId = new URLSearchParams(window.location.search).get('session_id');
    if (sessionId) localStorage.setItem(this.sessionKey(context), sessionId);
    return sessionId;
  }

  getSessionId(context: PaymentContext): string | null {
    return localStorage.getItem(this.sessionKey(context));
  }

  clearSessionId(context: PaymentContext): void {
    localStorage.removeItem(this.sessionKey(context));
  }

  private sessionKey(context: PaymentContext): string {
    const map: Record<PaymentContext, string> = {
      [PaymentContext.PLAN_CHANGE]:        'planChangeSessionId',
      [PaymentContext.AUTO_RENEW]:         'autoRenewSessionId',
      [PaymentContext.SUBSCRIPTION]:       'subscriptionSessionId',
      [PaymentContext.TOP_UP]:             'topUpSessionId',
      [PaymentContext.EQUIPMENT_PURCHASE]: 'equipmentPurchaseSessionId',
      [PaymentContext.ADHOC_INVOICE]:      'adhocInvoiceSessionId'
    };
    return map[context];
  }

  // ─── Braintree ────────────────────────────────────────────────────────────

  async initializeBraintree(): Promise<boolean> {
    const config = this.gatewayConfigSubject.value;
    if (config?.key !== 'token' || !config.token?.value) {
      console.error('[PaymentGateway] Braintree token not available'); return false;
    }
    try {
      const braintree = await import('braintree-web');
      this.braintreeClient = await (braintree as any).client.create({
        authorization: config.token.value
      });
      return true;
    } catch (err) {
      console.error('[PaymentGateway] Braintree init failed:', err); return false;
    }
  }

  async createBraintreeDropin(container: string, threeDSecure = false): Promise<any> {
    const config = this.gatewayConfigSubject.value;
    if (config?.key !== 'token' || !config.token?.value) {
      throw new Error('Braintree token not available');
    }
    const dropin = await import('braintree-web-drop-in');
    const opts: any = { authorization: config.token.value, vaultManager: true, container };
    if (threeDSecure) opts.threeDSecure = true;
    this.dropinInstance = await (dropin as any).create(opts);
    return this.dropinInstance;
  }

  async createBraintreeHostedFields(config: BraintreeHostedFieldsConfig): Promise<any> {
    if (this.hostedFieldsInitPromise) {
      return this.hostedFieldsInitPromise;
    }
    if (this.hostedFieldsInstance) {
      try {
        await this.hostedFieldsInstance.teardown();
      } catch (err) {}
      this.hostedFieldsInstance = null;
    }
    this.hostedFieldsInitPromise = (async () => {
      if (!this.braintreeClient) {
        const ok = await this.initializeBraintree();
        if (!ok) throw new Error('Braintree client initialization failed');
      }
      const braintree = await import('braintree-web');
      this.hostedFieldsInstance = await (braintree as any).hostedFields.create({
        client: this.braintreeClient,
        styles: config.styles || {},
        fields: config.fields
      });
      return this.hostedFieldsInstance;
    })();
    try {
      return await this.hostedFieldsInitPromise;
    } finally {
      this.hostedFieldsInitPromise = null;
    }
  }

  async createBraintreePaymentNonce(): Promise<string | null> {
    if (this.dropinInstance) {
      const result = await this.dropinInstance.requestPaymentMethod();
      return result.nonce;
    }
    if (!this.hostedFieldsInstance) {
      console.error('[PaymentGateway] No Braintree instance available'); return null;
    }
    const result = await this.hostedFieldsInstance.tokenize();
    return result.nonce;
  }

  async initializeVaultManager(): Promise<boolean> {
    if (!this.braintreeClient) {
      const ok = await this.initializeBraintree();
      if (!ok) return false;
    }
    try {
      const braintree = await import('braintree-web');
      this.vaultManagerInstance = await (braintree as any).vaultManager.create({
        client: this.braintreeClient
      });
      return true;
    } catch (err) {
      console.error('[PaymentGateway] Vault Manager init failed:', err);
      return false;
    }
  }

  async fetchVaultedPaymentMethods(): Promise<VaultedPaymentMethod[]> {
    if (!this.vaultManagerInstance) {
      const ok = await this.initializeVaultManager();
      if (!ok) return [];
    }
    try {
      const methods = await this.vaultManagerInstance.fetchPaymentMethods();
      return methods.map((method: any) => this.mapToVaultedPaymentMethod(method));
    } catch (err) {
      console.error('[PaymentGateway] Failed to fetch vaulted methods:', err);
      return [];
    }
  }

  private mapToVaultedPaymentMethod(method: any): VaultedPaymentMethod {
    const result: VaultedPaymentMethod = {
      nonce:       method.nonce,
      type:        method.type,
      description: method.description || '',
      details: {},
      default:     method.default || false
    };

    if (method.type === 'CreditCard') {
      result.details = {
        cardType:        method.details?.cardType,
        lastFour:        method.details?.lastFour,
        lastTwo:         method.details?.lastTwo,
        expirationMonth: method.details?.expirationMonth,
        expirationYear:  method.details?.expirationYear
      };
      result.description = `${method.details?.cardType || 'Card'} ending in ${method.details?.lastFour || '****'}`;
    } else if (method.type === 'PayPalAccount') {
      result.details = {
        email:     method.details?.email,
        payerInfo: method.details?.payerInfo
      };
      result.description = method.details?.email || 'PayPal Account';
    }

    return result;
  }

  async deleteVaultedPaymentMethod(nonce: string): Promise<boolean> {
    if (!this.vaultManagerInstance) {
      console.error('[PaymentGateway] Vault Manager not initialized');
      return false;
    }
    try {
      await this.vaultManagerInstance.deletePaymentMethod(nonce);
      return true;
    } catch (err) {
      console.error('[PaymentGateway] Failed to delete vaulted method:', err);
      return false;
    }
  }

  destroyBraintree(): void {
    if (this.dropinInstance) { this.dropinInstance.teardown().catch(() => {}); this.dropinInstance = null; }
    if (this.hostedFieldsInstance) { this.hostedFieldsInstance.teardown().catch(() => {}); this.hostedFieldsInstance = null; }
    if (this.vaultManagerInstance) { this.vaultManagerInstance = null; }
    this.braintreeClient = null;
    this.hostedFieldsInitPromise = null;
  }

  async resetBraintree(): Promise<void> {
    if (this.dropinInstance) {
      try { await this.dropinInstance.teardown(); } catch (err) {}
      this.dropinInstance = null;
    }
    if (this.hostedFieldsInstance) {
      try { await this.hostedFieldsInstance.teardown(); } catch (err) {}
      this.hostedFieldsInstance = null;
    }
    this.vaultManagerInstance = null;
    this.braintreeClient = null;
    this.hostedFieldsInitPromise = null;
  }

  // ─── Payload Formatting ───────────────────────────────────────────────────

  formatPaymentPayload(
    basePayload: Partial<PaymentPayload>,
    context: PaymentContext,
    cardDetails?: CardDetails,
    billingAddress?: BillingAddress
  ): PaymentPayload {
    const config   = this.gatewayConfigSubject.value;
    const flowType = config ? this.determinePaymentFlow(config) : PaymentFlowType.TRADITIONAL;
    const sessionId = this.getSessionId(context);

    let payload: PaymentPayload = { CustomerId: '', amount: 0, ...basePayload };

    if ((flowType === PaymentFlowType.STRIPE_REDIRECT || flowType === PaymentFlowType.BRAINTREE) && sessionId) {
      payload.paymentMethodToken = sessionId;
      delete payload.creditCard;
    } else if (cardDetails) {
      payload.creditCard = cardDetails;
    }

    if (billingAddress) payload.billingAddress = billingAddress;
    return payload;
  }
}
